import React, { useState, useRef, useContext } from 'react';
import { ButtonStyled, TextFieldStyled, DropDownStyled, useTokens } from '@nike/nike-design-system-components';
import { func, string, bool } from 'prop-types';
import { Box, Flex } from 'rebass';

import { TableServerSideContext } from '~COMPONENTS/tableServerSide/TableServerSideContext';
import { trimNSplit } from '../../../../util/trim';
import { parseProductCodes, isValidProductCode } from '../../utils';
import { SALES_ORGS } from '../../constants';

import Modal from '../../../../modal/Modal';

import BadgeStyled from '../../../../components/BadgeStyled';

const RETURNABILITY_AUTO = 'auto';
const RETURNABILITY_YES = 'returnable';
const RETURNABILITY_NO = 'nonreturnable';
const RETURNABILITY_OPTIONS = [
  { label: 'Default (PROMO = non-returnable)', value: RETURNABILITY_AUTO },
  { label: 'Returnable', value: RETURNABILITY_YES },
  { label: 'Non-returnable', value: RETURNABILITY_NO }
];

const AddProductsModal = ({ title, closeModal, isOpen, addProductsAction, hasSalesOrg }) => {
  const { refreshTableData } = useContext(TableServerSideContext);

  const [inputValue, setInputValue] = useState('');
  const [salesOrgValue, setSalesOrgValue] = useState(null);
  const [productCodes, setProductCodes] = useState([]);
  const [returnability, setReturnability] = useState({});

  const { validProductCodes, invalidProductCodes } = parseProductCodes(productCodes);

  const tokens = useTokens();
  const productsContainer = useRef();

  const clearInput = () => {
    setInputValue('');
    setProductCodes([]);
    setReturnability({});
  };

  const close = () => {
    closeModal();
    clearInput();
  };

  const enterProductCodes = e => {
    e.preventDefault();
    const deduped = new Set(productCodes.concat(trimNSplit(inputValue)));
    setProductCodes([...deduped]);
    setInputValue('');
    const element = productsContainer.current;
    setTimeout(() => {
      element.scrollTop = element.scrollHeight;
    }, 10);
  };

  const onAddProductInputChange = e => {
    setInputValue(e.target.value);
  };

  const onReturnabilityChange = productCode => e => {
    setReturnability(prev => ({ ...prev, [productCode]: e.target.value }));
  };

  // Only products with an explicit choice get a `returnable` value; the rest use the API default (PROMO rule)
  const buildReturnabilityPayload = codes =>
    codes.map(productCode => {
      const choice = returnability[productCode];
      return choice === RETURNABILITY_YES || choice === RETURNABILITY_NO
        ? { productCode, returnable: choice === RETURNABILITY_YES }
        : { productCode };
    });

  const onSalesOrgChange = e => {
    setSalesOrgValue(e.target.value);
  };

  const content = ({ isLoading }) => {
    const isTextFieldDisabled = isLoading || inputValue.length === 0;

    const removeProductCode = productCode => () => {
      const filtered = productCodes.filter(code => code !== productCode);
      setProductCodes(filtered);
      // Drop any explicit choice so a re-added code starts at Default
      setReturnability(prev => {
        const { [productCode]: removed, ...rest } = prev;
        return rest;
      });
    };

    return (
      <>
        <Box
          style={{ maxHeight: '390px', overflowY: 'scroll', width: '100%' }}
          ref={productsContainer}
          id='productsContainer'
        >
          {productCodes.map(
            (productCode, index) =>
              productCode && (
                <Flex alignItems='center' key={productCode} mb='8px'>
                  <div style={{ width: '40%' }}>
                    <BadgeStyled
                      // eslint-disable-next-line react/no-array-index-key
                      key={`${productCode}-${index}`}
                      text={productCode}
                      error={!isValidProductCode(productCode)}
                      block
                      onClose={removeProductCode(productCode)}
                      borderRadius='50px'
                      backgroundColor={tokens.podiumCdsColorBlack}
                      fontColor={tokens.podiumCdsColorWhite}
                    />
                  </div>
                  {!hasSalesOrg && isValidProductCode(productCode) && (
                    <Box ml='16px' width='220px'>
                      <DropDownStyled
                        id={`returnable-${productCode}`}
                        name={`returnable-${productCode}`}
                        label='Returnable'
                        options={RETURNABILITY_OPTIONS}
                        value={returnability[productCode] || RETURNABILITY_AUTO}
                        onChange={onReturnabilityChange(productCode)}
                      />
                    </Box>
                  )}
                </Flex>
              )
          )}
        </Box>

        <Box width='100%' mt='20px'>
          <form onSubmit={enterProductCodes} data-testid='addCatalogProducts-form'>
            <TextFieldStyled
              name='addCatalogProducts'
              id='addCatalogProducts'
              label='Product Codes'
              supportText='Paste a list of product codes here (separated by spaces).'
              value={inputValue}
              onChange={onAddProductInputChange}
              error={false}
              disabled={isTextFieldDisabled}
              autoComplete='off'
            />

            {hasSalesOrg && (
              <DropDownStyled
                id='salesOrg'
                name='salesOrg'
                label='Select Sales Org'
                options={SALES_ORGS}
                value={salesOrgValue}
                onChange={onSalesOrgChange}
              />
            )}
          </form>
        </Box>
      </>
    );
  };

  const footer = ({ isLoading, toggleIsLoading }) => {
    const hasValidProducts = invalidProductCodes.length < productCodes.length;
    const isSubmitButtonDisabled = isLoading || !hasValidProducts || (hasSalesOrg && !salesOrgValue);

    const handleSubmit = async () => {
      toggleIsLoading(true, 'Adding Product...');

      if (hasSalesOrg) {
        await addProductsAction(validProductCodes, salesOrgValue);
      } else {
        await addProductsAction(buildReturnabilityPayload(validProductCodes));
        refreshTableData();
      }

      clearInput();

      toggleIsLoading(false);
      closeModal();
    };

    return (
      <ButtonStyled
        data-testid='addProducts-submit-button'
        type='button'
        appearance='primary'
        background='light'
        size='small'
        className='mt7-sm'
        onClick={handleSubmit}
        disabled={isSubmitButtonDisabled}
      >
        {`Add ${validProductCodes.length} Products`}
      </ButtonStyled>
    );
  };

  return <Modal title={title} content={content} footer={footer} isOpen={isOpen} closeModal={close} unwrapped />;
};

AddProductsModal.propTypes = {
  title: string.isRequired,
  addProductsAction: func.isRequired,
  closeModal: func.isRequired,
  isOpen: bool.isRequired,
  hasSalesOrg: bool
};

AddProductsModal.defaultProps = {
  hasSalesOrg: false
};

export default AddProductsModal;
